import React, { Component } from "react";
import styled from "styled-components";
import { CButton } from "@appcues/component-library";
import FontIcon from "components/Common/Icons/FontIcon";
import { PAYMENT_CONFIRMATION_MODAL } from "constants/globals";
import { ONBOARD_MANAGER, PLAN_LIMITS, PRICES } from "constants/plans";
import { createPlanId } from "utils/stripe";

const SALES_PURCHASE_MODAL_APPCUES_FLOW_ID = "-LerJ1l3alV9we_zskMj";

class Plan extends Component {
  beginStripePurchase = () => {
    const {
      actions,
      plan,
      planInterval,
      price,
      purchasePlan,
      user
    } = this.props;

    const planId = createPlanId(price, plan.id, planInterval);

    purchasePlan(
      { id: planId, name: plan.name },
      {
        email: user.email,
        userId: user.id,
        accountId: user.accountId
      },
      actions.setPaymentFailed,
      actions.setPaymentSuccess
    );

    // Try to track analytics.
    if (window.analytics && window.analytics.track) {
      try {
        let trackProps = {
          ...plan,
          planInterval,
          SKU: planId
        };
        actions.trackEvent("Selected Plan", trackProps);
      } catch (e) {} // eslint-disable-line no-empty
    }
  };

  handlePurchaseButtonClick = () => {
    const {
      actions,
      planInterval,
      planLimit,
      plan,
      price,
      selfService
    } = this.props;

    const monthlyPrice = selfService
      ? PRICES.monthly[planLimit] &&
        PRICES.monthly[planLimit][plan.pricePlanId || plan.id]
      : "Let's talk";

    actions.showModal(PAYMENT_CONFIRMATION_MODAL, {
      showCancelButton: true,
      interval: planInterval,
      plan,
      planLimit,
      price,
      monthlyPrice,
      beginStripePurchase: this.beginStripePurchase,
      actions
    });
  };

  handleContactSalesButtonClick = () =>
    window.Appcues && window.Appcues.show(SALES_PURCHASE_MODAL_APPCUES_FLOW_ID);

  getPlanFeatures = () => {
    const { plan } = this.props;
    const standardHighList = [
      "1199-standard-monthly",
      "1199-standard-monthly",
      "7788-standard-yearly",
      "779-standard-monthly"
    ];

    if (standardHighList.includes(plan)) {
      const planFeatures = { ...plan.features, [ONBOARD_MANAGER]: true };
      return planFeatures;
    }
    return plan.features;
  };

  render() {
    const {
      index,
      plan,
      planLimit,
      planInterval,
      price,
      selfService
    } = this.props;

    const priceLetsTalk = price === "Let's talk";

    const selectedPlanLimit = PLAN_LIMITS.find(
      bracket => bracket.id === planLimit
    );
    const limitDisplay = selectedPlanLimit && selectedPlanLimit.name;

    const buttonLabel = `Select ${plan.name}`;
    const hasCsm = planLimit > 10000 || priceLetsTalk;

    const planFeatures = this.getPlanFeatures();

    let pricePostCaption = plan.pricePostCaption;
    if (!pricePostCaption && !priceLetsTalk && planInterval === "yearly") {
      pricePostCaption = "Paid annually";
    }

    let specialPrice = plan.price;
    if (!specialPrice && priceLetsTalk) {
      specialPrice = price;
    }

    return (
      <PlanWrapper key={plan.id + "-" + index}>
        <GradientBanner gradient={plan.gradient} />
        <PlanName color={plan.color}>{plan.name}</PlanName>
        {plan.pricePreCaption && (
          <StyledPricePreCaption>
            <span>{plan.pricePreCaption}</span>
          </StyledPricePreCaption>
        )}
        <PlanPrice>{specialPrice || `$${price}/mo`}</PlanPrice>
        {pricePostCaption && (
          <StyledPricePostCaption>
            <span>{pricePostCaption}</span>
          </StyledPricePostCaption>
        )}
        <PlanDescription>
          <span>{plan.description}</span>
        </PlanDescription>
        {plan.featuresCaption && (
          <StyledFeaturesCaption>{plan.featuresCaption}</StyledFeaturesCaption>
        )}
        <FeatureList>
          {plan.id === "essentials" && (
            <FeatureItem key={`${limitDisplay} MAUs`}>
              <FeatureCheck
                color={plan.color}
                className="fas fa-check-circle"
              />
              <div>{`${limitDisplay} MAUs`}</div>
            </FeatureItem>
          )}
          {Object.keys(planFeatures).map(feature => {
            const successManager = feature === ONBOARD_MANAGER;
            const selected = successManager ? hasCsm : plan.features[feature];

            return (
              <FeatureItem key={feature}>
                <FeatureCheck
                  selected={selected}
                  color={plan.color}
                  className="fas fa-check-circle"
                />
                <div>{feature}</div>
              </FeatureItem>
            );
          })}
        </FeatureList>
        {selfService ? (
          <CTAButton
            type="primary"
            backgroundColor={plan.color}
            onClick={this.handlePurchaseButtonClick}
          >
            {buttonLabel}
          </CTAButton>
        ) : (
          <CTAButton
            type="primary"
            backgroundColor={plan.color}
            onClick={this.handleContactSalesButtonClick}
          >
            Contact Sales
          </CTAButton>
        )}
      </PlanWrapper>
    );
  }
}

const planBorderRadius = "6px";

const PlanWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 48%;
  min-width: 258px;
  padding: 32px 28px 24px;
  margin-left: 20px;

  border: 1px solid ${props => props.theme["$gray-3"]};
  border-radius: ${planBorderRadius};

  text-align: center;
`;

const GradientBanner = styled.div`
  position: absolute;
  top: -1px;
  width: 100.5%;
  border-top-left-radius: ${planBorderRadius};
  border-top-right-radius: ${planBorderRadius};
  height: calc(${planBorderRadius} + 1px);
  background-color: transparent;
  background-image: ${props =>
    props.gradient ? props.theme[props.gradient] : props.theme["$galaxy"]};
`;

const PlanName = styled.h2`
  color: ${props =>
    props.color ? props.theme[props.color] : props.theme["$gray-7"]};
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  margin: 0;
  margin-bottom: 40px;
`;

const PlanPrice = styled.h3`
  font-weight: 300;
  font-size: 36px;
  line-height: 1em;
  margin: 0;
`;

const StyledPricePostCaption = styled.div`
  position: relative;
  color: ${props => props.theme["$gray-5"]};
  width: 100%;

  & > span {
    position: absolute;
    left: 0;
    white-space: nowrap;
    width: 100%;
    font-size: 14px;
    margin-top: 5px;
  }
`;

const StyledPricePreCaption = styled(StyledPricePostCaption)`
  & > span {
    bottom: 1px;
  }
`;

const PlanDescription = styled.div`
  margin: 48px 0 40px;
  position: relative;
  height: 52px;
  width: 100%;

  & > span {
    position: absolute;
    left: 0;
    right: 0;

    color: ${props => props.theme["$gray-7"]};
    font-weight: 700;
    line-height: 1.5rem;
  }
`;

const StyledFeaturesCaption = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: ${props => props.theme["$gray-7"]};
  line-height: 1.5rem;
  width: 100%;
  text-align: left;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
  text-align: left;
  width: 100%;
  min-height: 155px;
`;

const FeatureItem = styled.div`
  margin: 8px 0;
  font-size: 14px;
  display: flex;
  color: ${props => props.theme["$gray-7"]};
  align-items: baseline;
`;

const FeatureCheck = styled(FontIcon)`
  color: ${props =>
    props.color ? props.theme[props.color] : props.theme["$purple"]};
  margin-right: 10px;
`;

const CTAButton = styled(CButton)`
  margin-top: 40px;
  ${props =>
    props.backgroundColor &&
    `background-color: ${props.theme[props.backgroundColor]} !important;`};
`;
export default Plan;
